import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Plus, Trash2, Edit3, ShieldAlert } from "lucide-react";
import { AppLayout } from "../../../components/layout";
import { ContentCard } from "../../../components/ui";
import { BatchOverrideModal } from "../components/BatchOverrideModal";
import { inventoryManagementService } from "../../../services/inventoryManagementService";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { cn } from "../../../lib/utils";

const ALLOCATION_STATUS_KEYS = {
  Reserved: "status.reserved",
  "Partially Allocated": "inventory.statusPartiallyAllocated",
  Delivered: "inventory.statusDelivered",
  Revoked: "inventory.statusRevoked",
  Allocated: "inventory.statusAllocated",
  Partial: "inventory.statusPartial",
  Shortfall: "inventory.statusShortfall",
};

export function AllocateToCustomerPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [successToast, setSuccessToast] = useState("");

  // Options states
  const [productsList, setProductsList] = useState([]);
  const [customersList, setCustomersList] = useState([]);
  const [batchesDatabase, setBatchesDatabase] = useState([]);

  // Form Header States
  const allocationSource = "Manual"; // 'Manual' or 'IOF' (IOF is disabled)
  const [headerData, setHeaderData] = useState({
    date: "2026-08-04", // Default today
    iofNumber: "",
    customerId: "",
    invoiceNumber: "",
    invoiceDate: "",
    deliveryDate: "",
  });

  // Product Allocation Lines
  const [lines, setLines] = useState([
    {
      id: "line-1",
      productId: "",
      iofQty: 0,
      dispatchMethod: "FIFO", // FIFO or FEFO
      customAllocations: null, // Map of { batchNumber: qty } when overridden
    },
  ]);

  const [formErrors, setFormErrors] = useState({});

  // Batch override modal trigger states
  const [overrideModalOpen, setOverrideModalOpen] = useState(false);
  const [activeLineId, setActiveLineId] = useState(null);

  // Load dropdown lists and batches
  useEffect(() => {
    let active = true;
    setLoading(true);

    Promise.all([
      inventoryManagementService.getAllocationProducts(),
      inventoryManagementService.getAllocationCustomers(),
      inventoryManagementService.getBatchRecommendations(""), // Retrieves all batches
    ])
      .then(([p, c, b]) => {
        if (!active) return;
        setProductsList(p);
        setCustomersList(c);
        setBatchesDatabase(b);
        setLoading(false);
      })
      .catch((err) => {
        if (!active) return;
        console.error("Failed to load customer allocation lookups:", err);
        setError(t("validation.failedToLoadDropdownMetadata"));
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [t]);

  const handleHeaderChange = (key, value) => {
    setHeaderData((prev) => ({ ...prev, [key]: value }));
    if (formErrors[key]) {
      setFormErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  // Allocation line handlers
  const handleAddLine = () => {
    setLines((prev) => [
      ...prev,
      {
        id: `line-${Date.now()}`,
        productId: "",
        iofQty: 0,
        dispatchMethod: "FIFO",
        customAllocations: null,
      },
    ]);
  };

  const handleRemoveLine = (id) => {
    if (lines.length === 1) {
      alert(t("inventory.atLeastOneProductLine"));
      return;
    }
    setLines((prev) => prev.filter((l) => l.id !== id));
  };

  const handleLineProductChange = (id, productId) => {
    setLines((prev) =>
      prev.map((l) =>
        l.id === id
          ? {
              ...l,
              productId,
              iofQty: 0,
              customAllocations: null, // Reset overrides on product swap
            }
          : l
      )
    );
  };

  const handleLineQtyChange = (id, iofQty) => {
    setLines((prev) =>
      prev.map((l) =>
        l.id === id
          ? {
              ...l,
              iofQty: Math.max(0, Number(iofQty) || 0),
              customAllocations: null, // Clear overrides to trigger auto-reallocation
            }
          : l
      )
    );
  };

  const handleLineMethodChange = (id, method) => {
    setLines((prev) =>
      prev.map((l) =>
        l.id === id
          ? {
              ...l,
              dispatchMethod: method,
              customAllocations: null, // Reset overrides when dispatch method toggles
            }
          : l
      )
    );
  };

  const handleLineOverride = (id) => {
    setActiveLineId(id);
    setOverrideModalOpen(true);
  };

  const handleOverrideConfirm = (allocatedMap) => {
    setLines((prev) =>
      prev.map((l) =>
        l.id === activeLineId
          ? {
              ...l,
              customAllocations: Object.keys(allocatedMap).length > 0 ? allocatedMap : null,
            }
          : l
      )
    );
  };

  // Core FIFO/FEFO Allocation Engine for UI representation
  const computedLines = useMemo(() => {
    return lines.map((line) => {
      const product = productsList.find((p) => p.id === line.productId);
      const productBatches = batchesDatabase.filter((b) => b.productId === line.productId);

      // 1. Sort batches based on selected strategy
      const sortedBatches = [...productBatches];
      if (line.dispatchMethod === "FIFO") {
        // FIFO: Sort by Receipt Date oldest first
        sortedBatches.sort((a, b) => new Date(a.receiptDate) - new Date(b.receiptDate));
      } else {
        // FEFO: Sort by Expiry Date earliest first. Null values sort last.
        sortedBatches.sort((a, b) => {
          if (!a.warrantyEndDate && !b.warrantyEndDate) return 0;
          if (!a.warrantyEndDate) return 1;
          if (!b.warrantyEndDate) return -1;
          return new Date(a.warrantyEndDate) - new Date(b.warrantyEndDate);
        });
      }

      // 2. Allocate quantities
      let allocatedQty = 0;
      const batchEntries = [];

      if (line.customAllocations) {
        // Custom Manual Override mode
        productBatches.forEach((b) => {
          const alloc = line.customAllocations[b.batchNumber] || 0;
          if (alloc > 0) {
            allocatedQty += alloc;
            batchEntries.push({
              batchNumber: b.batchNumber,
              warehouse: b.warehouse,
              qty: alloc,
            });
          }
        });
      } else {
        // Auto allocation engine mode
        let remaining = line.iofQty;
        for (const b of sortedBatches) {
          if (remaining <= 0) break;
          const alloc = Math.min(remaining, b.availableQty);
          if (alloc > 0) {
            allocatedQty += alloc;
            batchEntries.push({
              batchNumber: b.batchNumber,
              warehouse: b.warehouse,
              qty: alloc,
            });
            remaining -= alloc;
          }
        }
      }

      const shortfall = Math.max(0, line.iofQty - allocatedQty);
      let status = "Allocated";
      if (line.iofQty > 0) {
        if (allocatedQty === 0) {
          status = "Shortfall";
        } else if (shortfall > 0) {
          status = "Partially Allocated";
        }
      } else {
        status = "—";
      }

      return {
        ...line,
        productName: product?.name || "",
        manufacturer: product?.manufacturer || "—",
        partNumber: product?.partNumber || "—",
        uom: product?.uom || "—",
        availableQty: product?.availableQty || 0,
        allocatedQty,
        shortfall,
        status,
        batchEntries,
        allBatches: productBatches,
        fifoPre: [...productBatches].sort((a, b) => new Date(a.receiptDate) - new Date(b.receiptDate)),
        fefoPre: [...productBatches].sort((a, b) => {
          if (!a.warrantyEndDate && !b.warrantyEndDate) return 0;
          if (!a.warrantyEndDate) return 1;
          if (!b.warrantyEndDate) return -1;
          return new Date(a.warrantyEndDate) - new Date(b.warrantyEndDate);
        }),
      };
    });
  }, [lines, productsList, batchesDatabase]);

  const activeLine = useMemo(() => {
    return computedLines.find((l) => l.id === activeLineId);
  }, [computedLines, activeLineId]);

  // live summary aggregation
  const summary = useMemo(() => {
    let reqTotal = 0;
    let allocTotal = 0;
    let shortfallTotal = 0;
    let entriesCount = 0;
    let productsCount = 0;

    computedLines.forEach((l) => {
      if (l.productId) {
        productsCount++;
        reqTotal += l.iofQty;
        allocTotal += l.allocatedQty;
        shortfallTotal += l.shortfall;
        entriesCount += l.batchEntries.length;
      }
    });

    return {
      productsCount,
      requestedQty: reqTotal,
      allocatedQty: allocTotal,
      shortfallQty: shortfallTotal,
      reservationEntries: entriesCount,
    };
  }, [computedLines]);

  const handleCancel = () => {
    navigate("/inventory-management/stock-position");
  };

  const handleSave = (e) => {
    e.preventDefault();
    const errors = {};

    // Validate header
    if (!headerData.customerId) {
      errors.customerId = t("validation.fieldRequired", { field: t("inventory.customerName") });
    }
    if (!headerData.invoiceNumber) {
      errors.invoiceNumber = t("validation.fieldRequired", { field: t("inventory.customerInvoiceId") });
    }
    if (!headerData.invoiceDate) {
      errors.invoiceDate = t("validation.fieldRequired", { field: t("dateTime.invoiceDate") });
    }
    if (!headerData.deliveryDate) {
      errors.deliveryDate = t("validation.fieldRequired", { field: t("dateTime.deliveryDate") });
    }

    // Validate lines
    const lineErrors = [];
    computedLines.forEach((l, index) => {
      if (!l.productId) {
        lineErrors[index] = t("validation.fieldRequired", { field: t("inventory.product") });
      } else if (l.iofQty <= 0) {
        lineErrors[index] = t("validation.qtyMustBeGreaterThanZero", { field: t("inventory.iofQty") });
      }
    });

    if (lineErrors.length > 0) {
      errors.lines = lineErrors;
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      // Alert user with validation error message
      alert(t("validation.validationErrors"));
      return;
    }

    setSubmitting(true);
    const payload = {
      allocationSource,
      date: headerData.date,
      iofNumber: headerData.iofNumber || null,
      customerId: headerData.customerId,
      customerName: customersList.find((c) => c.id === headerData.customerId)?.name,
      invoiceNumber: headerData.invoiceNumber,
      invoiceDate: headerData.invoiceDate,
      deliveryDate: headerData.deliveryDate,
      allocationSummary: summary,
      allocationLines: computedLines.map((l) => ({
        productId: l.productId,
        productName: l.productName,
        partNumber: l.partNumber,
        requestedQty: l.iofQty,
        allocatedQty: l.allocatedQty,
        shortfall: l.shortfall,
        status: l.status,
        dispatchMethod: l.dispatchMethod,
        isCustomOverridden: !!l.customAllocations,
        batchesUsed: l.batchEntries,
      })),
    };

    inventoryManagementService.createAllocation(payload)
      .then((res) => {
        setSuccessToast(t("inventory.allocationSavedSuccess"));
        setSubmitting(false);
        setTimeout(() => {
          setSuccessToast("");
          // Navigate to placeholder route
          navigate(`/inventory-management/allocation-detail/${res.allocationId || "1"}`);
        }, 1500);
      })
      .catch((err) => {
        console.error("Failed to save customer allocation payload:", err);
        setError(t("validation.allocationSaveError"));
        setSubmitting(false);
      });
  };

  const lineStatusDisplay = (status) => {
    if (status === "—") return "—";
    if (status === "Partially Allocated") return t(ALLOCATION_STATUS_KEYS.Partial);
    const key = ALLOCATION_STATUS_KEYS[status];
    return key ? t(key) : status;
  };

  return (
    <AppLayout
      breadcrumb={[
        { label: t("inventory.operations") },
        { label: t("inventory.inventoryManagement"), onClick: () => navigate("/inventory-management/stock-position") },
        { label: t("inventory.allocateToCustomer") },
      ]}
      className="space-y-6"
    >
      {/* Toast Alert Success popup */}
      {successToast && (
        <div className="fixed top-5 right-5 z-50 bg-emerald-600 text-white px-5 py-3 rounded-xl shadow-lg flex items-center gap-2 animate-in fade-in duration-300 font-sans font-bold text-xs">
          <span>✔️</span>
          <span>{successToast}</span>
        </div>
      )}

      {/* Override dialog modal mount */}
      <BatchOverrideModal
        open={overrideModalOpen}
        onOpenChange={setOverrideModalOpen}
        productName={activeLine?.productName}
        batches={activeLine?.allBatches}
        initialAllocation={activeLine?.customAllocations || {}}
        targetQty={activeLine?.iofQty || 0}
        onConfirm={handleOverrideConfirm}
      />

      {/* Custom Title and Back Nav */}
      <div className="space-y-1 font-sans">
        <button
          onClick={handleCancel}
          className="inline-flex items-center gap-1 text-[var(--color-primary)] text-xs font-bold hover:underline bg-transparent border-0 cursor-pointer focus:outline-none mb-2"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          <span>{t("formActions.backToStockJournal")}</span>
        </button>

        <h1 className="text-xl sm:text-2xl font-extrabold text-foreground tracking-tight">
          {t("inventory.allocateTitle")}
        </h1>
      </div>

      {loading ? (
        /* Form loading skeleton */
        <ContentCard bodyClassName="p-6 h-[450px] animate-pulse bg-gray-50/20" />
      ) : error ? (
        <div className="p-8 text-center text-[var(--color-error)] font-semibold text-sm border bg-white rounded-2xl">
          ⚠️ {error}
        </div>
      ) : (
        <form onSubmit={handleSave} className="space-y-6">
          {/* Card 1: Allocation Source (Radio Cards side-by-side) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 font-sans">
            {/* IOF Card (Disabled) */}
            <div className="rounded-2xl border border-gray-100 bg-gray-50/70 p-4 opacity-50 cursor-not-allowed flex items-start gap-3">
              <input
                type="radio"
                disabled
                checked={false}
                className="mt-1 h-4 w-4 border-gray-300 text-gray-400 focus:ring-0 cursor-not-allowed"
              />
              <div className="space-y-1">
                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wide">
                  {t("formActions.importFromIof")}
                </h4>
                <p className="text-[11px] text-gray-400 font-semibold leading-relaxed">
                  {t("formActions.importFromIofHint")}
                </p>
              </div>
            </div>

            {/* Manual Card (Enabled and Default Selected) */}
            <div className="rounded-2xl border-2 border-[var(--color-accent)] bg-white p-4 shadow-sm flex items-start gap-3">
              <input
                type="radio"
                checked={true}
                readOnly
                className="mt-1 h-4 w-4 border-gray-300 text-[var(--color-accent)] focus:ring-0 cursor-pointer"
              />
              <div className="space-y-1">
                <h4 className="text-xs font-bold text-foreground uppercase tracking-wide">
                  {t("inventory.enterManually")}
                </h4>
                <p className="text-[11px] text-[var(--color-text-secondary)] font-semibold leading-relaxed">
                  {t("inventory.enterManuallyHint")}
                </p>
              </div>
            </div>
          </div>

          {/* Card 2: Order Header Details */}
          <ContentCard title={t("inventory.orderHeader")} bodyClassName="p-6 font-sans">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-5">
              {/* Date */}
              <div className="space-y-1.5 col-span-1">
                <label className="text-[11px] font-bold uppercase tracking-wider text-[var(--color-text-secondary)]">
                  {t("dateTime.date")} <span className="text-rose-500">*</span>
                </label>
                <input
                  type="date"
                  value={headerData.date}
                  onChange={(e) => handleHeaderChange("date", e.target.value)}
                  className="w-full h-10 px-3 rounded-xl border border-[var(--color-text-border)] bg-white text-xs font-semibold focus:border-[var(--color-accent)] focus:outline-none"
                />
              </div>

              {/* IOF Number */}
              <div className="space-y-1.5 col-span-1">
                <label className="text-[11px] font-bold uppercase tracking-wider text-[var(--color-text-secondary)]">
                  {t("inventory.iofNo")}
                </label>
                <input
                  type="text"
                  value={headerData.iofNumber}
                  onChange={(e) => handleHeaderChange("iofNumber", e.target.value)}
                  placeholder={t("inventory.iofNumber")}
                  className="w-full h-10 px-3 rounded-xl border border-[var(--color-text-border)] bg-white text-xs font-semibold focus:border-[var(--color-accent)] focus:outline-none"
                />
                <span className="text-[10px] text-gray-400 block mt-1">
                  {t("dateTime.iofNotValidated")}
                </span>
              </div>

              {/* Customer Dropdown */}
              <div className="space-y-1.5 col-span-1">
                <label className="text-[11px] font-bold uppercase tracking-wider text-[var(--color-text-secondary)]">
                  {t("inventory.customerName")} <span className="text-rose-500">*</span>
                </label>
                <Select
                  value={headerData.customerId}
                  onValueChange={(val) => handleHeaderChange("customerId", val)}
                >
                  <SelectTrigger
                    className={`h-10 rounded-xl border bg-white text-xs font-semibold focus:ring-[var(--color-accent)] w-full ${
                      formErrors.customerId ? "border-rose-500" : "border-[var(--color-text-border)]"
                    }`}
                  >
                    <SelectValue placeholder={t("inventory.selectCustomer")} />
                  </SelectTrigger>
                  <SelectContent>
                    {customersList.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formErrors.customerId && (
                  <p className="text-[11px] text-rose-500 font-semibold mt-1">{formErrors.customerId}</p>
                )}
              </div>

              {/* Customer Invoice Number */}
              <div className="space-y-1.5 col-span-1">
                <label className="text-[11px] font-bold uppercase tracking-wider text-[var(--color-text-secondary)]">
                  {t("inventory.customerInvoiceId")} <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  value={headerData.invoiceNumber}
                  onChange={(e) => handleHeaderChange("invoiceNumber", e.target.value)}
                  className={`w-full h-10 px-3 rounded-xl border bg-white text-xs font-semibold focus:border-[var(--color-accent)] focus:outline-none ${
                    formErrors.invoiceNumber ? "border-rose-500" : "border-[var(--color-text-border)]"
                  }`}
                />
                {formErrors.invoiceNumber && (
                  <p className="text-[11px] text-rose-500 font-semibold mt-1">{formErrors.invoiceNumber}</p>
                )}
              </div>

              {/* Customer Invoice Date */}
              <div className="space-y-1.5 col-span-1">
                <label className="text-[11px] font-bold uppercase tracking-wider text-[var(--color-text-secondary)]">
                  {t("dateTime.invoiceDate")} <span className="text-rose-500">*</span>
                </label>
                <input
                  type="date"
                  value={headerData.invoiceDate}
                  onChange={(e) => handleHeaderChange("invoiceDate", e.target.value)}
                  className={`w-full h-10 px-3 rounded-xl border bg-white text-xs font-semibold focus:border-[var(--color-accent)] focus:outline-none ${
                    formErrors.invoiceDate ? "border-rose-500" : "border-[var(--color-text-border)]"
                  }`}
                />
                {formErrors.invoiceDate && (
                  <p className="text-[11px] text-rose-500 font-semibold mt-1">{formErrors.invoiceDate}</p>
                )}
              </div>

              {/* Delivery Date */}
              <div className="space-y-1.5 col-span-1">
                <label className="text-[11px] font-bold uppercase tracking-wider text-[var(--color-text-secondary)]">
                  {t("dateTime.deliveryDate")} <span className="text-rose-500">*</span>
                </label>
                <input
                  type="date"
                  value={headerData.deliveryDate}
                  onChange={(e) => handleHeaderChange("deliveryDate", e.target.value)}
                  className={`w-full h-10 px-3 rounded-xl border bg-white text-xs font-semibold focus:border-[var(--color-accent)] focus:outline-none ${
                    formErrors.deliveryDate ? "border-rose-500" : "border-[var(--color-text-border)]"
                  }`}
                />
                <span className="text-[10px] text-gray-400 block mt-1">
                  {t("inventory.outwardMovementHint")}
                </span>
                {formErrors.deliveryDate && (
                  <p className="text-[11px] text-rose-500 font-semibold mt-1">{formErrors.deliveryDate}</p>
                )}
              </div>
            </div>
          </ContentCard>

          {/* Card 3: Line Details & Recommendations */}
          <ContentCard
            title={t("inventory.lineDetails")}
            action={
              <div className="flex items-center gap-4 text-xs font-semibold font-sans">
                <span className="text-gray-400 font-bold uppercase text-[10px] tracking-wide">
                  {t("inventory.dispatchMethod")}
                </span>
                <span className="text-[10px] text-gray-300 font-medium hidden sm:inline">
                  {t("inventory.fifoFefoHint")}
                </span>
              </div>
            }
            bodyClassName="divide-y divide-[var(--color-text-border)]/45 font-sans"
          >
            {computedLines.map((line, index) => (
              <div key={line.id} className="p-4 sm:p-6 space-y-4 bg-white">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-end">
                  {/* Row Index */}
                  <div className="lg:col-span-1 text-xs font-extrabold text-foreground pb-3 font-mono">
                    #{index + 1}
                  </div>

                  {/* Product Dropdown Selector */}
                  <div className="lg:col-span-4 space-y-1.5">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-[var(--color-text-secondary)]">
                      {t("inventory.product")} <span className="text-rose-500">*</span>
                    </label>
                    <Select
                      value={line.productId}
                      onValueChange={(val) => handleLineProductChange(line.id, val)}
                    >
                      <SelectTrigger
                        className={cn(
                          "h-10 rounded-xl border bg-white text-xs font-semibold w-full",
                          formErrors.lines?.[index] ? "border-rose-500" : "border-[var(--color-text-border)]"
                        )}
                      >
                        <SelectValue placeholder={t("inventory.selectProduct")} />
                      </SelectTrigger>
                      <SelectContent>
                        {productsList.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* IOF Quantity Input */}
                  <div className="lg:col-span-2 space-y-1.5">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-[var(--color-text-secondary)]">
                      {t("inventory.iofQty")} <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={line.iofQty || ""}
                      onChange={(e) => handleLineQtyChange(line.id, e.target.value)}
                      placeholder="0"
                      className={cn(
                        "w-full h-10 px-3 rounded-xl border bg-white text-xs font-semibold focus:border-[var(--color-accent)] focus:outline-none",
                        formErrors.lines?.[index] ? "border-rose-500" : "border-[var(--color-text-border)]"
                      )}
                    />
                  </div>

                  {/* Read-Only: UOM */}
                  <div className="lg:col-span-1 space-y-1.5 text-center">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-[var(--color-text-secondary)] block">
                      {t("uom.uom")}
                    </label>
                    <span className="text-xs font-bold text-foreground block h-10 flex items-center justify-center font-mono">
                      {line.uom}
                    </span>
                  </div>

                  {/* Read-Only: Available Qty */}
                  <div className="lg:col-span-1 space-y-1.5 text-center">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-[var(--color-text-secondary)] block">
                      {t("status.available")}
                    </label>
                    <span className="text-xs font-bold text-foreground block h-10 flex items-center justify-center font-mono">
                      {line.availableQty}
                    </span>
                  </div>

                  {/* Read-Only: Part Number */}
                  <div className="lg:col-span-1 space-y-1.5 text-center">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-[var(--color-text-secondary)] block">
                      {t("inventory.partNo")}
                    </label>
                    <span className="text-xs font-semibold text-gray-500 block h-10 flex items-center justify-center truncate font-mono">
                      {line.partNumber}
                    </span>
                  </div>

                  {/* Read-Only Status */}
                  <div className="lg:col-span-1 space-y-1.5 text-center">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-[var(--color-text-secondary)] block">
                      {t("status.status")}
                    </label>
                    <div className="h-10 flex items-center justify-center">
                      {line.status === "Allocated" ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                          {lineStatusDisplay(line.status)}
                        </span>
                      ) : line.status === "Partially Allocated" ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200">
                          {lineStatusDisplay(line.status)}
                        </span>
                      ) : line.status === "Shortfall" ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-50 text-rose-700 border border-rose-200">
                          {lineStatusDisplay(line.status)}
                        </span>
                      ) : (
                        <span className="text-gray-400 font-bold">—</span>
                      )}
                    </div>
                  </div>

                  {/* Row Actions panel */}
                  <div className="lg:col-span-1 flex items-center justify-end gap-2 pb-1.5">
                    {line.productId && (
                      <button
                        type="button"
                        onClick={() => handleLineOverride(line.id)}
                        title={t("inventory.overrideRecommendations")}
                        className="p-2 rounded-lg border border-[var(--color-text-border)] text-gray-500 hover:text-[var(--color-primary)] hover:bg-gray-50 transition cursor-pointer"
                      >
                        <Edit3 className="h-4 w-4" />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => handleRemoveLine(line.id)}
                      title={t("formActions.deleteLine")}
                      className="p-2 rounded-lg border border-[var(--color-text-border)] text-gray-400 hover:text-rose-600 hover:bg-rose-50 transition cursor-pointer"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {/* Validation line specific error text */}
                {formErrors.lines?.[index] && (
                  <p className="text-[11px] text-rose-500 font-bold ml-8">
                    ⚠️ {formErrors.lines[index]}
                  </p>
                )}

                {/* Batch recommendations and allocations previews */}
                {line.productId && line.iofQty > 0 && (
                  <div className="pl-8 pt-3 border-t border-gray-100/70 space-y-4">
                    {/* Method toggler and display info */}
                    <div className="flex items-center justify-between flex-wrap gap-2 text-xs">
                      <div className="flex items-center gap-1.5">
                        <span className="font-bold text-gray-500">{t("inventory.allocationStrategy")}</span>
                        <div className="inline-flex p-0.5 bg-gray-100 rounded-lg">
                          <button
                            type="button"
                            onClick={() => handleLineMethodChange(line.id, "FIFO")}
                            className={cn(
                              "px-2 py-0.5 rounded text-[10px] font-bold cursor-pointer transition",
                              line.dispatchMethod === "FIFO" ? "bg-white text-[var(--color-primary)] shadow-sm" : "text-gray-400 hover:text-gray-600"
                            )}
                          >
                            {t("inventory.fifoLabel")}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleLineMethodChange(line.id, "FEFO")}
                            className={cn(
                              "px-2 py-0.5 rounded text-[10px] font-bold cursor-pointer transition",
                              line.dispatchMethod === "FEFO" ? "bg-white text-[var(--color-primary)] shadow-sm" : "text-gray-400 hover:text-gray-600"
                            )}
                          >
                            {t("inventory.fefoLabel")}
                          </button>
                        </div>
                      </div>

                      {line.customAllocations ? (
                        <span className="inline-flex items-center gap-1 text-[11px] text-amber-600 font-bold bg-amber-50 px-2 py-0.5 rounded-lg border border-amber-100 animate-pulse">
                          <span>🛠️</span>
                          <span>{t("inventory.customOverrideActive")}</span>
                        </span>
                      ) : (
                        <span className="text-[11px] text-gray-400 font-semibold">
                          {t("inventory.autoAllocating")}
                        </span>
                      )}
                    </div>

                    {/* Previews: FIFO vs FEFO Batches Recommendation Comparison */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* FIFO Pre */}
                      <div className="rounded-xl border border-gray-100 bg-gray-50/50 p-3 space-y-2">
                        <span className="text-[9px] font-extrabold uppercase tracking-wider text-gray-400 block border-b border-gray-200 pb-1">
                          {t("inventory.fifoRecommendationsQueue")}
                        </span>
                        <div className="space-y-1 max-h-[120px] overflow-y-auto font-mono text-[10px] font-semibold text-gray-600">
                          {line.fifoPre.map((b) => (
                            <div key={b.batchNumber} className="flex justify-between hover:bg-gray-100/50 p-0.5 rounded">
                              <span className="font-bold text-foreground">{b.batchNumber} ({b.warehouse})</span>
                              <span>{t("inventory.avail")} {b.availableQty} · {t("inventory.recd")} {b.receiptDate}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* FEFO Pre */}
                      <div className="rounded-xl border border-gray-100 bg-gray-50/50 p-3 space-y-2">
                        <span className="text-[9px] font-extrabold uppercase tracking-wider text-gray-400 block border-b border-gray-200 pb-1">
                          {t("inventory.fefoRecommendationsQueue")}
                        </span>
                        <div className="space-y-1 max-h-[120px] overflow-y-auto font-mono text-[10px] font-semibold text-gray-600">
                          {line.fefoPre.map((b) => (
                            <div key={b.batchNumber} className="flex justify-between hover:bg-gray-100/50 p-0.5 rounded">
                              <span className="font-bold text-foreground">{b.batchNumber} ({b.warehouse})</span>
                              <span>{t("inventory.avail")} {b.availableQty} · {t("inventory.exp")} {b.warrantyEndDate || t("common.none")}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Active Allocations Display */}
                    <div className="space-y-2 bg-[var(--color-workspace)]/20 p-3.5 rounded-xl border border-[var(--color-text-border)]/50">
                      <span className="text-[10px] font-extrabold uppercase tracking-wider text-[var(--color-primary)] block">
                        {t("inventory.activeBatchAllocationEntries")}
                      </span>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                        {line.batchEntries.length === 0 ? (
                          <div className="col-span-full py-2 text-center text-xs font-bold text-rose-500 flex items-center justify-center gap-1.5">
                            <ShieldAlert className="h-4 w-4" />
                            <span>{t("inventory.noAvailableBatches")}</span>
                          </div>
                        ) : (
                          line.batchEntries.map((entry) => (
                            <div key={entry.batchNumber} className="bg-white px-3 py-2 rounded-lg border border-gray-100 flex items-center justify-between text-xs shadow-sm">
                              <div className="font-sans">
                                <span className="font-mono font-bold text-foreground block">{entry.batchNumber}</span>
                                <span className="text-[10px] text-gray-400 font-semibold block">{entry.warehouse}</span>
                              </div>
                              <span className="font-mono font-bold text-[var(--color-primary)] text-sm">{entry.qty}</span>
                            </div>
                          ))
                        )}
                      </div>
                      {line.shortfall > 0 && (
                        <div className="text-[11px] text-amber-700 bg-amber-50 px-2 py-1 rounded border border-amber-100 flex items-center justify-between font-bold">
                          <span>⚠️ {t("inventory.shortfallWarning")}</span>
                          <span className="font-mono">{line.shortfall}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}

            {/* Add product line bar */}
            <div className="p-4 sm:p-6 bg-gray-50/50 flex items-center">
              <button
                type="button"
                onClick={handleAddLine}
                className="inline-flex items-center gap-1.5 h-9 px-4 rounded-xl border border-[var(--color-primary)]/40 hover:border-[var(--color-primary)] bg-white text-xs font-bold text-[var(--color-primary)] hover:bg-[var(--color-workspace)]/30 transition cursor-pointer"
              >
                <Plus className="h-4 w-4" />
                <span>{t("formActions.addProduct")}</span>
              </button>
            </div>
          </ContentCard>

          {/* Card 4: Allocation Summary card */}
          <ContentCard title={t("inventory.allocationSummary")} bodyClassName="p-6 font-sans bg-gray-50/20">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
              <div className="bg-white p-3 rounded-xl border border-gray-100 shadow-sm">
                <span className="text-[9px] font-extrabold uppercase tracking-wider text-gray-400 block">
                  {t("inventory.numberOfProducts")}
                </span>
                <span className="text-lg font-extrabold text-foreground block mt-1 font-mono">
                  {summary.productsCount}
                </span>
              </div>

              <div className="bg-white p-3 rounded-xl border border-gray-100 shadow-sm">
                <span className="text-[9px] font-extrabold uppercase tracking-wider text-gray-400 block">
                  {t("formActions.requestedQty")}
                </span>
                <span className="text-lg font-extrabold text-foreground block mt-1 font-mono">
                  {summary.requestedQty}
                </span>
              </div>

              <div className="bg-white p-3 rounded-xl border border-gray-100 shadow-sm">
                <span className="text-[9px] font-extrabold uppercase tracking-wider text-emerald-500 block">
                  {t("inventory.allocatedQty")}
                </span>
                <span className="text-lg font-extrabold text-emerald-600 block mt-1 font-mono">
                  {summary.allocatedQty}
                </span>
              </div>

              <div className="bg-white p-3 rounded-xl border border-gray-100 shadow-sm">
                <span className="text-[9px] font-extrabold uppercase tracking-wider text-rose-500 block">
                  {t("inventory.shortfallQty")}
                </span>
                <span className={cn("text-lg font-extrabold block mt-1 font-mono", summary.shortfallQty > 0 ? "text-rose-600 animate-pulse" : "text-gray-400")}>
                  {summary.shortfallQty}
                </span>
              </div>

              <div className="bg-white p-3 rounded-xl border border-gray-100 shadow-sm col-span-2 md:col-span-1">
                <span className="text-[9px] font-extrabold uppercase tracking-wider text-[var(--color-primary)] block">
                  {t("inventory.reservationEntries")}
                </span>
                <span className="text-lg font-extrabold text-[var(--color-primary)] block mt-1 font-mono">
                  {summary.reservationEntries}
                </span>
              </div>
            </div>
          </ContentCard>

          {/* Form Actions Footer Panel */}
          <div className="flex items-center justify-end gap-3 pt-6 border-t border-[var(--color-text-border)]/50">
            <button
              type="button"
              onClick={handleCancel}
              className="h-10 px-5 rounded-xl border border-[var(--color-text-border)] text-[var(--color-text-secondary)] font-bold text-xs hover:bg-gray-50 transition cursor-pointer"
            >
              {t("formActions.cancel")}
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="h-10 px-6 rounded-xl bg-[var(--color-primary)] hover:bg-[var(--color-accent)] text-white font-bold text-xs transition shadow-[0_8px_20px_-8px_rgba(30,51,131,0.5)] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? t("inventory.saving") : t("formActions.saveAllocation")}
            </button>
          </div>
        </form>
      )}
    </AppLayout>
  );
}
